import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, ListChecks, Users, Clock, TrendUp, Star, Buildings, MapPinLine } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ listings: 0, pending: 0, leads: 0, newLeads: 0 });
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState({ listings: [], projects: [], communities: [] });

  useEffect(() => {
    (async () => {
      try {
        const [ls, leads, fl, fp, fc] = await Promise.all([
          api.get("/admin/listings"),
          api.get("/admin/leads"),
          api.get("/listings", { params: { featured: true, limit: 20 } }),
          api.get("/projects", { params: { featured: true } }),
          api.get("/communities", { params: { featured: true, limit: 20 } }),
        ]);
        const items = ls.data.items;
        const leadItems = leads.data.items;
        setStats({
          listings: items.filter((l) => l.status === "published").length,
          pending: items.filter((l) => l.status === "pending").length,
          leads: leadItems.length,
          newLeads: leadItems.filter((l) => l.stage === "new").length,
        });
        setRecent(leadItems.slice(0, 6));
        setFeatured({
          listings: fl.data.items || [],
          projects: fp.data.items || [],
          communities: fc.data.items || [],
        });
      } catch { /* no-op */ }
    })();
  }, []);

  return (
    <div className="p-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Overview</div>
          <h1 className="font-serif text-4xl">Welcome, {user.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/admin/listings/new" className="flex items-center gap-2 bg-white text-white px-5 py-3 text-sm hover:bg-[#001588] hover:text-black transition-colors" data-testid="dashboard-new-listing">
          <Plus size={16} /> New listing
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Kpi icon={ListChecks} label="Published mandates" value={stats.listings} testId="kpi-published" />
        <Kpi icon={Clock} label="Pending approvals" value={stats.pending} testId="kpi-pending" />
        <Kpi icon={Users} label="Total leads" value={stats.leads} testId="kpi-leads" />
        <Kpi icon={TrendUp} label="New leads" value={stats.newLeads} testId="kpi-new-leads" />
      </div>

      <div className="border border-[#E5E7EB] bg-white p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">Recent enquiries</h2>
          <Link to="/admin/leads" className="text-sm text-gray-600 hover:text-black">View all →</Link>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {recent.length === 0 && <div className="text-sm text-gray-500">No enquiries yet.</div>}
          {recent.map((l) => (
            <div key={l.id} className="py-4 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{l.name}</div>
                <div className="text-gray-500 text-xs">{l.email} · {l.phone}</div>
              </div>
              <div className="text-xs text-gray-500 max-w-xs truncate">{l.listing_title || l.interest || "General enquiry"}</div>
              <span className="text-xs uppercase tracking-wider bg-gray-100 px-2 py-1">{l.stage}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Featured on Home — quick shortcuts */}
      <div className="mt-8 border border-[#E5E7EB] bg-white p-8" data-testid="dash-featured-panel">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">
              <Star size={14} weight="fill" className="text-[#ffffff]" /> Featured on Home
            </div>
            <h2 className="font-serif text-2xl">What visitors see on the homepage</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Each list below controls a section on <code>/</code>. Toggle the <em>Featured</em> checkbox on any item
              in its admin page to add / remove it here.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <FeaturedCol
            icon={ListChecks}
            title="Featured residences"
            manageTo="/admin/listings"
            emptyLabel="No mandates featured yet — open Listings and check Featured on any item."
            items={featured.listings.map((l) => ({ key: l.id, label: l.title, hint: l.community, to: `/admin/listings/${l.id}/edit` }))}
          />
          <FeaturedCol
            icon={Buildings}
            title="Featured projects"
            manageTo="/admin/projects"
            emptyLabel="No projects featured yet — open Projects and check Featured on any."
            items={featured.projects.map((p) => ({ key: p.slug, label: p.name, hint: p.community, to: `/admin/projects/${p.slug}/edit` }))}
          />
          <FeaturedCol
            icon={MapPinLine}
            title="Featured communities"
            manageTo="/admin/communities"
            emptyLabel="No communities featured yet — open Communities and check Featured on any."
            items={featured.communities.map((c) => ({ key: c.slug, label: c.name, hint: c.emirate, to: `/admin/communities/${c.slug}/edit` }))}
          />
        </div>
      </div>
    </div>
  );
}

function FeaturedCol({ icon: Icon, title, items, manageTo, emptyLabel }) {
  return (
    <div className="border border-neutral-200 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-[#001588]" weight="thin" />
          <span className="text-xs uppercase tracking-wider text-neutral-600">{title}</span>
        </div>
        <span className="text-[10px] bg-[#ffffff]/20 text-[#8A6F3D] px-2 py-0.5 uppercase tracking-wider">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-neutral-500 italic py-4">{emptyLabel}</div>
      ) : (
        <ul className="space-y-2 flex-1">
          {items.slice(0, 5).map((i) => (
            <li key={i.key} className="text-sm border-b border-neutral-100 pb-2">
              <Link to={i.to} className="font-medium text-neutral-800 hover:text-[#001588] block truncate">{i.label}</Link>
              {i.hint && <div className="text-xs text-neutral-500 truncate">{i.hint}</div>}
            </li>
          ))}
          {items.length > 5 && <li className="text-xs text-neutral-500">+{items.length - 5} more…</li>}
        </ul>
      )}
      <Link to={manageTo} className="mt-4 text-xs uppercase tracking-[0.2em] text-[#001588] border-t border-neutral-100 pt-3 hover:underline">
        Manage all →
      </Link>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <Icon size={22} className="text-gray-400" weight="thin" />
      </div>
      <div className="font-serif text-4xl mb-1">{value}</div>
      <div className="text-xs tracking-wider uppercase text-gray-500">{label}</div>
    </div>
  );
}
